import React from 'react';
import { TopBar } from '../../components/layout/TopBar';
import { Header } from '../../components/layout/Header';
import { Navigation } from '../../components/layout/Navigation';
import { MobileStickyBar } from '../../components/layout/MobileStickyBar';
import { Footer } from '../../components/layout/Footer';
import { useScreenInit } from '../../useScreenInit';
import { FileText, ExternalLink, Info, RefreshCw, PackageCheck, Ruler, Building2, AlertTriangle, Phone, ArrowRight } from 'lucide-react';
const PDF_RESOURCES = [{
  title: 'USPS Resources — Federal Register 2020 Notice',
  description: 'Notice of Changes to Postal Service Standard 4C (Vol. 85, No. 244, Dec. 18, 2020), revising the apartment parcel locker ratio.',
  href: 'https://midatlanticmailboxes.com/uploaded/files/FederalRegistry2020.pdf?direct=1'
}, {
  title: 'Postal Code Update — Current USPS Codes Outlined (2024)',
  description: 'A plain-language outline of current USPS code expectations for multi-family rehab/replacement, 4C unit approvals, and parcel locker requirements.',
  href: 'https://midatlanticmailboxes.com/uploaded/files/USPScodeOutline2024.pdf?direct=1'
}, {
  title: 'Postal Code Addendum — USPS Code Outline Addendum (2024)',
  description: 'An addendum clarifying replacement and renovation guidance for legacy horizontal and vertical centralized mail units.',
  href: 'https://midatlanticmailboxes.com/uploaded/files/USPScodeoutlinedaddendum2024.pdf?direct=1'
}];
const KEY_UPDATES = [{
  icon: RefreshCw,
  text: 'Renovations and remodels should be reviewed against current USPS standards.'
}, {
  icon: AlertTriangle,
  text: 'Legacy 4B/4B+ horizontal or vertical centralized units may no longer be appropriate for full replacement or renovation projects.'
}, {
  icon: Building2,
  text: 'Current compliant centralized mailbox options are typically USPS STD 4C mailboxes or CBUs.'
}, {
  icon: PackageCheck,
  text: 'USPS parcel locker ratio changed from 1:10 to 1:5 for apartment community buildings as of the 2020 Standard 4C update.'
}, {
  icon: Ruler,
  text: 'Parcel lockers must be 15 inches high to count toward the 1:5 tenant-door-to-parcel-locker ratio.'
}];
const REACH_ROWS = [{
  type: 'USPS Mailbox',
  fMin: '28"',
  sMin: '28"',
  fMax: '67"',
  sMax: '67"'
}, {
  type: 'USPS Parcel Locker',
  fMin: '15"',
  sMin: '15"',
  fMax: '67"',
  sMax: '67"'
}, {
  type: 'ADA Mail & Parcel',
  fMin: '15"',
  sMin: '15"',
  fMax: '48"',
  sMax: '48"'
}, {
  type: 'FHA Mail & Parcel',
  fMin: '15"',
  sMin: '9"',
  fMax: '48"',
  sMax: '54"'
}];
export function USPSResources() {
  useScreenInit();
  return <div className="min-h-screen flex flex-col bg-brand-background" data-id="element-1872">
      <TopBar data-id="element-1873" />
      <Header data-id="element-1874" />
      <Navigation data-id="element-1875" />

      <main className="flex-grow" data-id="element-1876">
        {/* Hero */}
        <section className="bg-white border-b border-brand-border py-16 lg:py-24" data-id="element-1877">
          <div className="max-w-7xl mx-auto px-4 text-center" data-id="element-1878">
            <span className="inline-block py-1 px-3 rounded-full bg-brand-goldSoft text-brand-charcoal text-xs font-bold tracking-wide mb-4 uppercase border border-brand-gold/30" data-id="element-1879">
              Resource Guidance
            </span>
            <h1 className="text-4xl lg:text-5xl xl:text-6xl font-serif font-bold text-brand-charcoal mb-6 leading-tight" data-id="element-1880">
              USPS Resources for Centralized Mailbox Planning
            </h1>
            <p className="text-lg lg:text-xl text-brand-muted max-w-3xl mx-auto leading-relaxed" data-id="element-1881">
              Guidance for multi-family replacement and renovation projects —
              covering 4C and CBU systems, parcel locker ratios, and reach
              ranges. These materials summarize current USPS standards to help
              you plan a compliant project.
            </p>
          </div>
        </section>

        {/* Intro Notice */}
        <section className="py-10 bg-brand-background" data-id="element-1882">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1883">
            <div className="bg-white border border-brand-border rounded-xl p-6 flex flex-col md:flex-row gap-5 items-start shadow-sm" data-id="element-1884">
              <div className="w-12 h-12 rounded-full bg-brand-goldSoft text-brand-maroon flex items-center justify-center shrink-0" data-id="element-1885">
                <Info size={24} data-id="element-1886" />
              </div>
              <div data-id="element-1887">
                <h2 className="text-lg font-bold text-brand-charcoal mb-2" data-id="element-1888">
                  How to use these resources
                </h2>
                <p className="text-brand-muted text-sm leading-relaxed" data-id="element-1889">
                  These resources help owners, managers, architects, and
                  contractors understand USPS mailbox standards for centralized
                  delivery. This is resource guidance, not legal or regulatory
                  advice — final approval can depend on USPS and local
                  Postmaster review. We always recommend confirming
                  project-specific requirements with your local USPS Postmaster
                  or with MidAtlantic Mailboxes before ordering or installing.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Key updates at a glance */}
        <section className="py-12 bg-white border-b border-brand-border" data-id="element-1890">
          <div className="max-w-7xl mx-auto px-4" data-id="element-1891">
            <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal mb-8 text-center" data-id="element-1892">
              Key Updates at a Glance
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1893">
              {KEY_UPDATES.map(({
              icon: Icon,
              text
            }) => <div key={text} className="bg-brand-background border border-brand-border rounded-xl p-6 flex flex-col" data-id="element-1894">
                  <div className="w-11 h-11 rounded-full bg-brand-goldSoft text-brand-maroon flex items-center justify-center mb-4" data-id="element-1895">
                    <Icon size={22} data-id="element-1896" />
                  </div>
                  <p className="text-brand-charcoal text-sm leading-relaxed" data-id="element-1897">
                    {text}
                  </p>
                </div>)}
            </div>
          </div>
        </section>

        {/* Download source documents */}
        <section className="py-16 bg-brand-goldWash border-b border-brand-border" data-id="element-1898">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1899">
            <div className="text-center max-w-2xl mx-auto mb-10" data-id="element-1900">
              <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal mb-3" data-id="element-1901">
                Download the Source Documents
              </h2>
              <p className="text-brand-charcoal/80" data-id="element-1902">
                View or download the original USPS reference PDFs that these
                summaries are based on.
              </p>
            </div>
            <div className="grid gap-4" data-id="element-1903">
              {PDF_RESOURCES.map(pdf => <a key={pdf.href} href={pdf.href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-4 p-5 bg-white rounded-xl border border-brand-border hover:border-brand-maroon hover:shadow-sm transition-all group" data-id="element-1904">
                  <div className="flex items-center gap-4" data-id="element-1905">
                    <div className="w-12 h-12 rounded-full bg-brand-goldSoft text-brand-maroon flex items-center justify-center shrink-0" data-id="element-1906">
                      <FileText size={22} data-id="element-1907" />
                    </div>
                    <div data-id="element-1908">
                      <h3 className="font-bold text-brand-charcoal group-hover:text-brand-maroon transition-colors" data-id="element-1909">
                        {pdf.title}
                      </h3>
                      <p className="text-sm text-brand-muted" data-id="element-1910">
                        {pdf.description}
                      </p>
                    </div>
                  </div>
                  <ExternalLink size={18} className="text-gray-400 group-hover:text-brand-maroon shrink-0" data-id="element-1911" />
                </a>)}
            </div>
          </div>
        </section>

        {/* Replacement & renovation guidance */}
        <section className="py-16 bg-white border-b border-brand-border" data-id="element-1912">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1913">
            <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal mb-8" data-id="element-1914">
              Replacement & Renovation Guidance
            </h2>

            <div className="space-y-6 text-brand-muted leading-relaxed" data-id="element-1915">
              <div className="bg-brand-background border border-brand-border rounded-xl p-6" data-id="element-1916">
                <h3 className="font-bold text-brand-charcoal mb-2" data-id="element-1917">
                  Multi-family rehab & replacements
                </h3>
                <p className="text-sm mb-3" data-id="element-1918">
                  Historically, USPS approved 1:1 replacement for legacy 4B/4B+
                  horizontal units when there were no changes in layout, unit,
                  design, or location. USPS has since updated the Postal
                  Operations Manual to communicate that any remodel or
                  renovation project should comply with current standards and be
                  upgraded to 4Cs or CBUs.
                </p>
                <p className="text-sm" data-id="element-1919">
                  The 1:1 replacement allowance now applies to repair parts and
                  damaged doors that don't require full unit replacement.
                </p>
              </div>

              <div className="bg-brand-maroon/5 border border-brand-maroon/20 rounded-xl p-6" data-id="element-1920">
                <h3 className="font-bold text-brand-maroon mb-2 flex items-center gap-2" data-id="element-1921">
                  <AlertTriangle size={18} data-id="element-1922" /> Legacy 4B/4B+ units &amp;
                  non-approved 4C mounts
                </h3>
                <p className="text-sm text-brand-charcoal/80 mb-3" data-id="element-1923">
                  Vertical and Horizontal mailboxes do NOT meet current USPS
                  requirements for centralized mail delivery. When an apartment
                  community or other multi-unit dwelling is renovated, the
                  property should replace obsolete 4B+ receptacles with current
                  USPS STD 4C mailboxes or CBU mailboxes. Renovations occurring
                  anywhere within the building (not just the mail room) may
                  prompt an upgrade to current USPS standards.
                </p>
                <p className="text-sm text-brand-charcoal/80" data-id="element-1924">
                  USPS specifically states that 4C pedestal and cabinet (Depot)
                  mount units are NOT approved. The approved install method for
                  4C units is recessed in the wall. Surface mounts are not
                  specified in the bulletin; some offices use discretion, while
                  others enforce that surface mounts are not an approved install
                  method.
                  <span className="block mt-2 text-xs italic" data-id="element-1925">
                    Reference: Section 632.621 — USPS POM (Postal Operations
                    Manual).
                  </span>
                </p>
              </div>

              <div className="bg-brand-background border border-brand-border rounded-xl p-6" data-id="element-1926">
                <h3 className="font-bold text-brand-charcoal mb-2" data-id="element-1927">
                  Parcel locker requirements
                </h3>
                <ul className="space-y-2 text-sm" data-id="element-1928">
                  <li className="flex items-start gap-2" data-id="element-1929">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1930"></span>
                    <span data-id="element-1931">
                      As of July 2020, USPS requires a minimum 1:5 tenant-door
                      to parcel-locker ratio for multi-family communities
                      (changed from the prior 1:10 ratio).
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1932">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1933"></span>
                    <span data-id="element-1934">
                      Parcel lockers must be 15 inches high to comply with the
                      1:5 ratio.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1935">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1936"></span>
                    <span data-id="element-1937">
                      The 4CADD-10 unit (a popular 100% ADA unit) does not
                      comply with the 1:5 ratio because of its smaller parcel
                      locker — it was compliant before July 2020 under the old
                      1:10 ratio. The recommended ADA unit is the 4CADD-09.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Reach & installation planning */}
        <section className="py-16 bg-brand-background border-b border-brand-border" data-id="element-1938">
          <div className="max-w-5xl mx-auto px-4" data-id="element-1939">
            <h2 className="text-2xl lg:text-3xl font-serif font-bold text-brand-charcoal mb-3" data-id="element-1940">
              Reach & Installation Planning
            </h2>
            <p className="text-brand-muted mb-8 text-sm" data-id="element-1941">
              Minimum / maximum reach overview across USPS, ADA, and FHA
              guidelines. ADA specifies that at least 5% of mailboxes meet
              accessibility guidelines.
            </p>

            {/* Desktop / wide: table */}
            <div className="hidden md:block overflow-x-auto bg-white border border-brand-border rounded-xl shadow-sm" data-id="element-1942">
              <table className="w-full text-sm" data-id="element-1943">
                <thead data-id="element-1944">
                  <tr className="bg-brand-charcoal text-white text-left" data-id="element-1945">
                    <th className="px-4 py-3 font-semibold" data-id="element-1946">Type</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-1947">Forward Min</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-1948">Side Min</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-1949">Forward Max</th>
                    <th className="px-4 py-3 font-semibold" data-id="element-1950">Side Max</th>
                  </tr>
                </thead>
                <tbody data-id="element-1951">
                  {REACH_ROWS.map((row, i) => <tr key={row.type} className={i % 2 === 0 ? 'bg-white' : 'bg-brand-background'} data-id="element-1952">
                      <td className="px-4 py-3 font-semibold text-brand-charcoal" data-id="element-1953">
                        {row.type}
                      </td>
                      <td className="px-4 py-3 text-brand-muted" data-id="element-1954">{row.fMin}</td>
                      <td className="px-4 py-3 text-brand-muted" data-id="element-1955">{row.sMin}</td>
                      <td className="px-4 py-3 text-brand-muted" data-id="element-1956">{row.fMax}</td>
                      <td className="px-4 py-3 text-brand-muted" data-id="element-1957">{row.sMax}</td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Mobile: stacked cards */}
            <div className="md:hidden space-y-4" data-id="element-1958">
              {REACH_ROWS.map(row => <div key={row.type} className="bg-white border border-brand-border rounded-xl shadow-sm overflow-hidden" data-id="element-1959">
                  <div className="bg-brand-charcoal text-white px-4 py-2.5 font-semibold text-sm" data-id="element-1960">
                    {row.type}
                  </div>
                  <dl className="grid grid-cols-2 gap-px bg-brand-border" data-id="element-1961">
                    <div className="bg-white px-4 py-3" data-id="element-1962">
                      <dt className="text-xs uppercase tracking-wide text-brand-muted mb-0.5" data-id="element-1963">
                        Forward Min
                      </dt>
                      <dd className="font-semibold text-brand-charcoal" data-id="element-1964">
                        {row.fMin}
                      </dd>
                    </div>
                    <div className="bg-white px-4 py-3" data-id="element-1965">
                      <dt className="text-xs uppercase tracking-wide text-brand-muted mb-0.5" data-id="element-1966">
                        Side Min
                      </dt>
                      <dd className="font-semibold text-brand-charcoal" data-id="element-1967">
                        {row.sMin}
                      </dd>
                    </div>
                    <div className="bg-white px-4 py-3" data-id="element-1968">
                      <dt className="text-xs uppercase tracking-wide text-brand-muted mb-0.5" data-id="element-1969">
                        Forward Max
                      </dt>
                      <dd className="font-semibold text-brand-charcoal" data-id="element-1970">
                        {row.fMax}
                      </dd>
                    </div>
                    <div className="bg-white px-4 py-3" data-id="element-1971">
                      <dt className="text-xs uppercase tracking-wide text-brand-muted mb-0.5" data-id="element-1972">
                        Side Max
                      </dt>
                      <dd className="font-semibold text-brand-charcoal" data-id="element-1973">
                        {row.sMax}
                      </dd>
                    </div>
                  </dl>
                </div>)}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8" data-id="element-1974">
              <div className="bg-white border border-brand-border rounded-xl p-6" data-id="element-1975">
                <h3 className="font-bold text-brand-charcoal mb-3" data-id="element-1976">
                  Planning for all specifications
                </h3>
                <ul className="space-y-2 text-sm text-brand-muted" data-id="element-1977">
                  <li className="flex items-start gap-2" data-id="element-1978">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1979"></span>
                    <span data-id="element-1980">
                      To meet ALL installation specifications, plan for mailbox
                      modules that fit between 28 inches and 48 inches above
                      finished floor.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1981">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1982"></span>
                    <span data-id="element-1983">
                      Modules containing both mailboxes and parcel lockers can
                      be installed between 15 inches and 48 inches above
                      finished floor.
                    </span>
                  </li>
                </ul>
              </div>
              <div className="bg-white border border-brand-border rounded-xl p-6" data-id="element-1984">
                <h3 className="font-bold text-brand-charcoal mb-3" data-id="element-1985">
                  Installation height reference
                </h3>
                <ul className="space-y-2 text-sm text-brand-muted" data-id="element-1986">
                  <li className="flex items-start gap-2" data-id="element-1987">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1988"></span>
                    <span data-id="element-1989">
                      <strong className="text-brand-charcoal" data-id="element-1990">
                        Max height
                      </strong>{' '}
                      measures the center lock of the highest tenant door: 67"
                      (USPS / partial 5% ADA), 54" (FHA), or 48" (100% ADA) from
                      finished floor.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1991">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1992"></span>
                    <span data-id="element-1993">
                      <strong className="text-brand-charcoal" data-id="element-1994">
                        Access door
                      </strong>{' '}
                      measures the center of the master access door lock: 36" to
                      48" above finished floor.
                    </span>
                  </li>
                  <li className="flex items-start gap-2" data-id="element-1995">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-maroon mt-1.5 shrink-0" data-id="element-1996"></span>
                    <span data-id="element-1997">
                      <strong className="text-brand-charcoal" data-id="element-1998">
                        Minimum install height
                      </strong>{' '}
                      measures from the bottom shelf by compartment type: tenant
                      door 28", parcel locker 15" from finished floor.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 lg:py-20 bg-brand-charcoal text-center" data-id="element-1999">
          <div className="max-w-3xl mx-auto px-4" data-id="element-2000">
            <h2 className="text-3xl font-serif font-bold text-white mb-4" data-id="element-2001">
              Planning a 4C or CBU Project?
            </h2>
            <p className="text-gray-300 mb-8" data-id="element-2002">
              For help choosing 4C/CBU equipment or planning a compliant
              replacement, contact MidAtlantic Mailboxes. We'll help confirm
              requirements and provide a free estimate.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-2003">
              <a href="tel:301-620-7585" className="bg-brand-gold hover:bg-brand-gold/90 text-brand-charcoal px-8 py-3.5 rounded-md font-bold transition-colors shadow-sm w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-2004">
                <Phone size={18} data-id="element-2005" /> Call 301-620-7585
              </a>
              <a href="/ContactUs.html" className="bg-transparent border-2 border-white hover:bg-white hover:text-brand-charcoal text-white px-8 py-3.5 rounded-md font-bold transition-colors shadow-sm w-full sm:w-auto flex items-center justify-center gap-2" data-id="element-2006">
                Contact & FAQs <ArrowRight size={16} data-id="element-2007" />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-2008" />
      <MobileStickyBar data-id="element-2009" />
    </div>;
}